import { isNull, isUndefined, mergeWith } from 'lodash'

import { logger } from '~/debug/logger'

import type { SourceAttributeValue } from '../types'

export type FormatterInputScalar = number | string | null | undefined
export type FormatterOptions = Partial<{
  longForm: boolean
  sigFigs: number
  units: string
  omitUnits: boolean
  invalidStr: string
  export: boolean
}>

export type FormatterHook<OwnOptions = {}> = () => FormatterFunction<OwnOptions>
export type FormatterFunction<OwnOptions = {}, Input = FormatterInputScalar | Partial<SourceAttributeValue>> = (
  value?: Input,
  options?: FormatterOptions & OwnOptions
) => string

/**
 * Merges the provided formatter options into the default options. If a formatter option is null
 * or undefined, the default option will be used.
 */
function mergeOptions<T>(defaults: T, options?: Partial<T>): T {
  return mergeWith({}, defaults, options, (objValue, srcValue) => {
    return isNull(srcValue) ? objValue : srcValue
  })
}

type CombineOptionsArgs<T extends FormatterOptions> = {
  defaults: T
  exports: T
  options?: Partial<T>
  value?: FormatterInputScalar | SourceAttributeValue
}

export function combineOptions<T extends FormatterOptions>({
  value,
  options,
  defaults,
  exports
}: CombineOptionsArgs<T>) {
  let combined

  if (options?.export) {
    if (!isUndefined(options.omitUnits) || !isUndefined(options.invalidStr)) {
      logger.warn('omitUnits and invalidStr are ignored when export is true')
    }
    combined = mergeOptions(exports, options)
  } else {
    combined = mergeOptions(defaults, options)
  }

  const [parsedValue, parsedUnits] = isSourceAttributeValue(value)
    ? [value.value, value.units]
    : [value, combined.units]

  return { ...combined, value: parsedValue, units: parsedUnits }
}

function isSourceAttributeValue(value: unknown): value is SourceAttributeValue {
  if (!value || typeof value !== 'object') return false
  return 'units' in value && 'value' in value
}
